import moment from 'moment'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import { requestCommunityMemberGrowthReport } from '../../../../../actions/reportsActions'
import { deserialize } from '../../../../../utility/serialization'
import { DATETIME_FORMAT, getMomentInCommunityTimezone } from '../../../../../utility/datesHelper'
import { COMMON_ERRORS } from '../../../../../misc/Constants'
import { buildCommunityYearRangeFunctions } from '../../communityYearRanges'

import Loader from '../../../../common/loader/Loader'
import Select from '../../../../common/form/Select'
import CheckBox from '../../../../common/form/CheckBox'
import MemberGrowthChart from './MemberGrowthChart'

import businessDashboardClasses from '../BusinessDashboard.module.scss'
import classes from './MemberGrowthWidget.module.scss'

const CHART_DATA_TYPE = {
  students: 'students',
  families: 'families',
}

const buildMemberGrowthYearRangeFunctions = (createdAt) => {
  const ranges = buildCommunityYearRangeFunctions(createdAt)
  const currentYear = moment()
    .year()

  if (ranges[currentYear]) {
    const baseLabel = ranges[currentYear].textFunc()
    ranges[currentYear] = {
      textFunc: () => baseLabel,
      valueFunc: () => ({
        startDate: moment()
          .subtract(11, 'months')
          .startOf('month')
          .toDate(),
        endDate: new Date(),
      }),
    }
  }

  return ranges
}

const SingleStatistics = ({ value, topLabel, bottomLabel }) => (
  <div className={classnames('d-flex flex-column text-center', businessDashboardClasses.statistics)}>
    <span className={businessDashboardClasses.value}>{value}</span>
    <span className={businessDashboardClasses.topLabel}>{topLabel}</span>
    <span className={businessDashboardClasses.bottomLabel}>{bottomLabel}</span>
  </div>
)

SingleStatistics.propTypes = {
  value: PropTypes.number.isRequired,
  topLabel: PropTypes.string.isRequired,
  bottomLabel: PropTypes.string.isRequired,
}

const formatGrowthPercentage = (ratio) => {
  if (ratio === null || ratio === undefined) {
    return null
  }
  return Intl.NumberFormat('en-US', { style: 'percent', signDisplay: 'exceptZero', maximumFractionDigits: 1 })
    .format(ratio)
}

const MemberGrowthWidget = () => {
  const community = useSelector(state => state.user.current_user.communities[0])
  const yearRangeFunctions = useMemo(
    () => buildMemberGrowthYearRangeFunctions(community?.created_at),
    [community?.created_at]
  )
  const yearSelectOptions = useMemo(
    () => Object.keys(yearRangeFunctions)
      .reverse()
      .map(key => ({
        label: yearRangeFunctions[key].textFunc(),
        value: key,
      })),
    [yearRangeFunctions]
  )
  const defaultFilters = useMemo(() => ({
    yearOption: yearSelectOptions[0],
  }), [yearSelectOptions])

  const [filters, setFilters] = useState(defaultFilters)
  const [chartDataType, setChartDataType] = useState(CHART_DATA_TYPE.students)
  const [showOnlyNew, setShowOnlyNew] = useState(false)
  const [isLoadingChartData, setIsLoadingChartData] = useState(true)
  const [data, setData] = useState()

  useEffect(() => {
    setIsLoadingChartData(true)

    const { startDate, endDate } = yearRangeFunctions[filters.yearOption.value].valueFunc()

    requestCommunityMemberGrowthReport({
      communityId: community?.id,
      startDate: getMomentInCommunityTimezone(moment(startDate), community?.timezone, DATETIME_FORMAT)
        .toDate(),
      endDate: getMomentInCommunityTimezone(moment(endDate), community?.timezone, DATETIME_FORMAT)
        .toDate(),
      chartDataType,
    })
      .then(res => {
        setData(deserialize(res.data.report.data))
        setIsLoadingChartData(false)
      })
      .catch(() => toast.error(COMMON_ERRORS[500]))
  }, [community, yearRangeFunctions, filters.yearOption, chartDataType])

  const handleSelectYearOption = (yearOption) => {
    if (!yearOption) {
      return
    }
    setFilters(prev => ({ ...prev, yearOption }))
  }

  const growthLabel = useMemo(() => {
    if (!data) {
      return null
    }
    return formatGrowthPercentage(data.studentsAnnualGrowthPercentage)
  }, [data])
  const growthIsNegative = data?.studentsAnnualGrowthPercentage < 0

  return (
    <section className={businessDashboardClasses.widget}>
      <div className={classnames('d-flex justify-content-between align-items-center', businessDashboardClasses.titleWrapper)}>
        <h2 className={businessDashboardClasses.title}>Member Growth</h2>
        <Select
          id='memberGrowthYearSelect'
          classNames={{
            control: () => classes.yearSelectControl,
            singleValue: () => classes.yearSelectSingleValue,
            menuList: () => classes.yearSelectMenuList,
          }}
          options={yearSelectOptions}
          value={filters.yearOption}
          onChange={handleSelectYearOption}
          isSearchable={false}
        />
      </div>
      <div className='d-flex justify-content-between align-items-center'>
        <div>
          <button
            className={classnames('btn-tab', { active: chartDataType === CHART_DATA_TYPE.students })}
            onClick={() => setChartDataType(CHART_DATA_TYPE.students)}
          >
            Students
          </button>
          <button
            className={classnames('btn-tab ml-3', { active: chartDataType === CHART_DATA_TYPE.families })}
            onClick={() => setChartDataType(CHART_DATA_TYPE.families)}
          >
            Families
          </button>
        </div>
        {growthLabel && (
          <span className={classnames(classes.growthLabel, { [classes.negative]: growthIsNegative })}>
            {growthLabel} member growth from previous year
          </span>
        )}
      </div>
      {isLoadingChartData ? (
        <div className='flex-grow-1 d-flex justify-content-center align-items-center'>
          <Loader>Loading data</Loader>
        </div>
      ) : (
        <>
          <div className={classnames('d-flex justify-content-between', businessDashboardClasses.sectionWrapper)}>
            <SingleStatistics
              value={data.totalStudentsAllTime}
              topLabel='Total Students'
              bottomLabel='(All-Time)'
            />
            <SingleStatistics
              value={data.newStudentsInPeriod}
              topLabel='New Students'
              bottomLabel={`(In ${filters.yearOption.value})`}
            />
            <SingleStatistics
              value={data.totalFamiliesAllTime}
              topLabel='Total Families'
              bottomLabel='(All-Time)'
            />
            <SingleStatistics
              value={data.newFamiliesInPeriod}
              topLabel='New Families'
              bottomLabel={`(In ${filters.yearOption.value})`}
            />
          </div>
          <div className={classes.checkboxRow}>
            <CheckBox
              id='memberGrowthShowOnlyNew'
              label='Show only new members'
              isSelected={showOnlyNew}
              onClick={() => setShowOnlyNew(prev => !prev)}
            />
            <div className={classes.legend}>
              <span className={classes.legendItem}>
                <span className={classnames(classes.legendDot, classes.orange)} />
                New {chartDataType === CHART_DATA_TYPE.students ? 'Students' : 'Families'}
              </span>
              {!showOnlyNew && (
                <span className={classes.legendItem}>
                  <span className={classnames(classes.legendDot, classes.slateGray)} />
                  Existing {chartDataType === CHART_DATA_TYPE.students ? 'Students' : 'Families'}
                </span>
              )}
            </div>
          </div>
          <div className={businessDashboardClasses.chartWrapper}>
            <MemberGrowthChart
              monthlyData={data.monthlyData}
              chartDataType={chartDataType}
              showOnlyNew={showOnlyNew}
            />
          </div>
        </>
      )}
    </section>
  )
}

export default MemberGrowthWidget
